/**
 * Compatibility shim: restores `BsDropdownModule.forRoot()`.
 *
 * WHY THIS EXISTS
 * ---------------
 * ngx-bootstrap 21 removed the static `forRoot()` from its modules. Where our
 * own code called it (BsDatepickerModule) the compiler caught it, but
 * `ngx-intl-tel-input` also calls `BsDropdownModule.forRoot()` *internally*,
 * at module-evaluation time:
 *
 *   const dropdownModuleForRoot = BsDropdownModule.forRoot();
 *   @NgModule({ imports: [CommonModule, FormsModule, ReactiveFormsModule, dropdownModuleForRoot] })
 *
 * That is invisible to our build (it is pre-compiled JS inside the package) and
 * only fails in the browser with:
 *
 *   Uncaught TypeError: ...BsDropdownModule.forRoot is not a function
 *
 * There is no version combination that fixes this properly:
 *   - Angular 21+ requires ngx-bootstrap 21.x, and *every* 21.x release has
 *     removed forRoot() (present in 20.0.2, gone from 21.0.1 onwards).
 *   - *Every* published ngx-intl-tel-input, up to and including the latest
 *     17.0.0, still calls BsDropdownModule.forRoot().
 *
 * WHY THIS IS SAFE
 * ----------------
 * In ngx-bootstrap 20.0.2 the method was a pure no-op wrapper:
 *
 *   static forRoot() { return { ngModule: BsDropdownModule, providers: [] }; }
 *
 * It contributed no providers, so re-adding exactly that shape restores the old
 * behaviour precisely rather than approximating it. The guard means this becomes
 * inert the moment ngx-bootstrap reinstates the method.
 *
 * ORDERING
 * --------
 * This module must be evaluated before anything imports ngx-intl-tel-input.
 * It is imported as the first statement of src/main.ts; ES module evaluation is
 * depth-first in import order, so this runs before AppModule and its transitive
 * imports.
 *
 * REMOVE WHEN
 * -----------
 * ngx-intl-tel-input stops calling forRoot(), or it is replaced with a wrapper
 * around intl-tel-input directly (already a dependency). Track it as tech debt:
 * this is a third-party API being monkey-patched.
 */
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

type ForRootCompatible = {
  forRoot?: () => { ngModule: unknown; providers: unknown[] };
};

const dropdownModule = BsDropdownModule as unknown as ForRootCompatible;

if (typeof dropdownModule.forRoot !== 'function') {
  dropdownModule.forRoot = () => ({
    ngModule: BsDropdownModule,
    providers: [],
  });
}
